import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EmptyState } from '../components/patterns/EmptyState';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Package, FileText, Target, TrendingUp, ArrowRight, Sparkles,
  BarChart3, Zap, DollarSign, Copy, Wand2, Users, Megaphone,
  CheckCircle, AlertTriangle, Clock, Radar, RefreshCw, Activity,
  Loader2, Bell, ShoppingBag, Percent, Undo2, Store, Trophy,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

// ── Vendas / Conversão / Devolução (Missão Autopiloto Simples) ──────────
// Iter 81: nunca calcula nada sem dado real vindo do backend. Vendas e
// devolução/cancelamento vêm de `/integrations/{meli,shopee}/dashboard`
// (mesmos endpoints já usados por MeliDashboardPage), somando só contas
// realmente conectadas. Conversão fica "indisponível" -- nenhum endpoint
// atual cruza impressões/visitas com vendas, então nunca inventamos uma
// fórmula para preencher o espaço.
const MKT_LABELS = { mercadolivre: 'Mercado Livre', shopee: 'Shopee' };

const StatMini = ({ label, value, hint }) => (
  <div className="rounded-lg border border-zinc-100 bg-zinc-50/60 p-3">
    <p className="text-[11px] text-zinc-500">{label}</p>
    <p className="text-lg font-bold text-zinc-950 font-['Manrope'] mt-0.5">{value}</p>
    {hint && <p className="text-[10px] text-zinc-400 mt-0.5">{hint}</p>}
  </div>
);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KPICard = ({ icon: Icon, title, value, subtitle, onClick, alert, className = '' }) => (
  <button
    type="button"
    className={`group relative min-h-[132px] p-4 sm:p-5 text-left bg-white hover:bg-zinc-50/70 transition-colors focus-visible:z-10 ${className}`}
    onClick={onClick}
  >
    <div className="flex h-full flex-col justify-between gap-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[12px] sm:text-[13px] font-semibold text-zinc-500 leading-tight">{title}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${alert ? 'border-red-200 bg-red-50 text-red-600' : 'border-zinc-200 bg-zinc-50 text-zinc-600'} group-hover:border-zinc-300`}>
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
      </div>
      <div>
        <p className="text-[28px] leading-none font-bold text-zinc-950 font-['Manrope'] tracking-[-0.04em]">{value}</p>
        <p className={`text-[11px] mt-2 min-h-[16px] ${alert ? 'text-red-600 font-medium' : 'text-zinc-400'}`}>
          {subtitle || 'Total registrado'}
        </p>
      </div>
    </div>
  </button>
);

const QuickAction = ({ icon: Icon, title, description, onClick }) => (
  <button
    type="button"
    className="group w-full flex items-center gap-4 rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-[0_1px_2px_rgba(10,10,11,0.035)] hover:border-zinc-300 hover:shadow-md transition-all"
    onClick={onClick}
  >
    <div className="w-10 h-10 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-700 flex-shrink-0 group-hover:bg-zinc-950 group-hover:text-white group-hover:border-zinc-950 transition-colors">
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
    </div>
    <div className="min-w-0 flex-1">
      <h3 className="font-semibold text-zinc-900 text-sm leading-tight">{title}</h3>
      <p className="text-xs text-zinc-500 mt-1 truncate">{description}</p>
    </div>
    <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all" />
  </button>
);

const ActivityItem = ({ item }) => {
  const typeMap = {
    LISTING: { icon: Sparkles, color: 'text-zinc-600', label: 'Anúncio' },
    ADS_STRATEGY: { icon: Megaphone, color: 'text-zinc-600', label: 'Estratégia ADS' },
    AUTOPILOT_REGEN: { icon: Wand2, color: 'text-zinc-600', label: 'Auto Piloto' },
  };
  const t = typeMap[item.type] || typeMap.LISTING;
  const Icon = t.icon;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-zinc-100 last:border-0">
      <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
        <Icon className={`w-3.5 h-3.5 ${t.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-800 truncate">{item.title}</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">{new Date(item.created_at).toLocaleString('pt-BR')}</p>
      </div>
      <Badge className={`text-xs flex-shrink-0 ${
        item.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
        item.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
        'bg-amber-100 text-amber-700'
      }`}>
        {item.status === 'APPROVED' ? 'Aprovado' : item.status === 'REJECTED' ? 'Rejeitado' : 'Pendente'}
      </Badge>
    </div>
  );
};

export const DashboardPage = () => {
  const [rtData, setRtData] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [integrationsStatus, setIntegrationsStatus] = useState(null);
  const [meliDashboard, setMeliDashboard] = useState(null);
  const [shopeeDashboard, setShopeeDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [rtRes, summaryRes, statusRes, meliRes, shopeeRes] = await Promise.all([
        axios.get(`${API}/dashboard/realtime`).catch(() => ({ data: null })),
        axios.get(`${API}/bunker-tasks/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/integrations/status`).catch(() => ({ data: null })),
        // Iter 81: mesmos endpoints reais já usados em MeliDashboardPage --
        // agregam pedidos, receita e top produtos direto das coleções
        // sincronizadas (meli_orders/shopee_orders). Nunca calculado aqui.
        axios.get(`${API}/integrations/meli/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/integrations/shopee/dashboard`).catch(() => ({ data: null })),
      ]);
      setRtData(rtRes.data);
      setTaskSummary(summaryRes.data);
      setIntegrationsStatus(statusRes.data);
      setMeliDashboard(meliRes.data?.success ? meliRes.data : null);
      setShopeeDashboard(shopeeRes.data?.success ? shopeeRes.data : null);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = rtData?.kpis || {};
  const recentActivity = rtData?.recent_activity || [];
  const alerts = (rtData?.alerts || []).filter(Boolean);

  // Iter 80.1 (P0): no mock data — chart renders ONLY real backend history.
  // When the Collector ETL has not produced a market-growth series yet, we
  // surface an empty state with a CTA to the Collector page.
  const marketChartData = Array.isArray(rtData?.market_growth_history)
    ? rtData.market_growth_history
    : [];

  const moduleData = [
    { name: 'Criador', value: kpis.total_drafts || 0 },
    { name: 'Aprovações', value: kpis.pending_approvals || 0 },
    { name: 'Oport.', value: kpis.high_opportunity || 0 },
    { name: 'Crítico', value: kpis.critical_products || 0 },
  ];

  // ── Vendas / devolução / top produtos: só com dado real ──────────────
  const meliConnected = Boolean(integrationsStatus?.integrations?.mercadolivre?.connected);
  const shopeeConnected = Boolean(integrationsStatus?.integrations?.shopee?.connected);
  const anyConnected = meliConnected || shopeeConnected;

  const meliOrders = meliDashboard?.metrics?.orders || null;
  const shopeeOrders = shopeeDashboard?.metrics?.orders || null;

  const totalOrders = (meliOrders?.total || 0) + (shopeeOrders?.total || 0);
  const ordersLast7d = (meliOrders?.last_7_days || 0) + (shopeeOrders?.last_7_days || 0);
  const hasOrderData = totalOrders > 0;

  // Cancelamento/devolução: única leitura de status já sincronizado nos
  // pedidos reais. MELI só expõe "cancelled" no pedido (devolução real vive
  // em meli_claims, fora do escopo desta entrega); Shopee expõe
  // cancelled/in_cancel/to_return -- por isso o rótulo cobre os dois casos
  // em vez de afirmar "devolução" onde só temos cancelamento.
  const meliCancelled = meliOrders?.by_status?.cancelled?.count || 0;
  const shopeeCancelReturn = ['cancelled', 'in_cancel', 'to_return'].reduce(
    (sum, key) => sum + (shopeeOrders?.by_status?.[key]?.count || 0), 0,
  );
  const totalCancelReturn = meliCancelled + shopeeCancelReturn;
  const cancelReturnPct = hasOrderData ? ((totalCancelReturn / totalOrders) * 100) : null;

  // Achado "H" (revisão de segurança, PR #22): "Receita total" somava TODOS
  // os pedidos sincronizados, inclusive cancelados/devolvidos, o que
  // afirmava uma receita maior do que a real. Os dois backends já expõem o
  // valor monetário POR status em `by_status.<status>.amount` (mesma
  // agregação que já alimenta os contadores de cancelamento acima) -- então
  // filtramos a receita pelos MESMOS status de cancelamento/devolução em vez
  // de só relabelar o número bruto.
  const meliCancelledAmount = meliOrders?.by_status?.cancelled?.amount || 0;
  const shopeeCancelReturnAmount = ['cancelled', 'in_cancel', 'to_return'].reduce(
    (sum, key) => sum + (shopeeOrders?.by_status?.[key]?.amount || 0), 0,
  );
  const meliNetRevenue = (meliOrders?.total_revenue || 0) - meliCancelledAmount;
  const shopeeNetRevenue = (shopeeOrders?.total_revenue || 0) - shopeeCancelReturnAmount;
  const netRevenue = meliNetRevenue + shopeeNetRevenue;

  const topProducts = [
    ...(meliDashboard?.metrics?.top_products || []).map((p) => ({ ...p, marketplace: 'mercadolivre' })),
    ...(shopeeDashboard?.metrics?.top_products || []).map((p) => ({ ...p, marketplace: 'shopee' })),
  ]
    .filter((p) => (p.sold_quantity || 0) > 0)
    .sort((a, b) => (b.sold_quantity || 0) - (a.sold_quantity || 0))
    .slice(0, 5);

  return (
    <Layout>
      <div data-testid="dashboard-page" className="bk-page space-y-8 md:space-y-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 pt-1">
          <div>
            <p className="bk-eyebrow mb-2">Operação</p>
            <h1 className="text-[28px] md:text-[34px] leading-tight font-bold text-zinc-950 font-['Manrope'] tracking-[-0.04em]">Visão geral</h1>
            <p className="text-zinc-500 text-sm mt-2 max-w-xl">
              Acompanhe os números essenciais e acesse rapidamente as rotinas da sua operação.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2 w-full md:w-auto">
            <Button variant="outline" onClick={fetchData} className="w-full sm:w-auto">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => navigate('/listing-creator')} className="w-full sm:w-auto bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="create-listing-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Novo anúncio
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-2">
            {alerts.map((alert, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                alert.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-zinc-100 border-zinc-200 text-zinc-700'
              }`}>
                <div className="flex items-center gap-2 text-sm">
                  {alert.type === 'warning' ? <AlertTriangle className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                  {alert.message}
                </div>
                {alert.action && (
                  <Button size="sm" variant="ghost" onClick={() => navigate(alert.action)} className="text-xs">
                    Ver <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bunker Tasks summary -- Missão WhatsApp Omnichannel: resumo real,
            vindo de GET /bunker-tasks/summary, com acesso direto às tarefas.
            Nunca inventa indicador financeiro ou de vendas. */}
        {taskSummary && (
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(10,10,11,0.035)]">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-zinc-200">
              <KPICard
                icon={Activity} title="Em andamento" value={taskSummary.in_progress}
                subtitle="Sendo processadas agora" onClick={() => navigate('/activities')}
              />
              <KPICard
                icon={Clock} title="Aguardando você" value={taskSummary.waiting_input}
                subtitle="Falta uma informação sua" onClick={() => navigate('/activities')}
                alert={taskSummary.waiting_input > 0}
              />
              <KPICard
                icon={CheckCircle} title="Aguardando aprovação" value={taskSummary.waiting_approval}
                subtitle="Prontas para você confirmar" onClick={() => navigate('/activities')}
                alert={taskSummary.waiting_approval > 0}
              />
              <KPICard
                icon={AlertTriangle} title="Falhas" value={taskSummary.failed}
                subtitle="Precisam de atenção" onClick={() => navigate('/activities')}
                alert={taskSummary.failed > 0}
              />
              <KPICard
                icon={ShoppingBag} title="Publicados" value={taskSummary.completed}
                subtitle="Anúncios concluídos" onClick={() => navigate('/activities')}
                className="col-span-2 md:col-span-1"
              />
            </div>
          </div>
        )}

        {/* KPIs */}
        {loading ? (
          <div className="flex items-center justify-center h-36 rounded-xl border border-zinc-200 bg-white">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(10,10,11,0.035)]">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-zinc-200">
            <KPICard
              icon={Package} title="Produtos" value={kpis.total_products || 0}
              onClick={() => navigate('/products')}
            />
            <KPICard
              icon={Copy} title="Rascunhos" value={kpis.total_drafts || 0}
              onClick={() => navigate('/drafts')}
            />
            <KPICard
              icon={CheckCircle} title="Aprovações" value={kpis.pending_approvals || 0}
              subtitle="aguardando revisão"
              onClick={() => navigate('/approvals')} alert={kpis.pending_approvals > 0}
            />
            <KPICard
              icon={Zap} title="Aprovados Hoje" value={kpis.approved_today || 0}
              subtitle="nas últimas 24h" onClick={() => navigate('/approvals')}
            />
            <KPICard
              icon={Target} title="Alta oportunidade" value={kpis.high_opportunity || 0}
              subtitle="score acima de 70" onClick={() => navigate('/opportunities')}
            />
            <KPICard
              icon={AlertTriangle} title="Críticos" value={kpis.critical_products || 0}
              subtitle="exigem atenção"
              onClick={() => navigate('/autopilot')} alert={kpis.critical_products > 0}
            />
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <p className="bk-eyebrow mb-1.5">Atalhos</p>
              <h2 className="bk-section-title text-lg">Ações rápidas</h2>
            </div>
            <span className="hidden sm:block text-xs text-zinc-400">Rotinas mais utilizadas</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <QuickAction
              icon={Sparkles} title="Criar com IA"
              description="BUNKER gera anúncio otimizado"
              onClick={() => navigate('/listing-creator')}
            />
            <QuickAction
              icon={Copy} title="Copiar anúncio"
              description="URL ou entrada manual"
              onClick={() => navigate('/listing-copier')}
            />
            <QuickAction
              icon={Megaphone} title="Inteligência de ADS"
              description="Analisa quando investir"
              onClick={() => navigate('/ads-intelligence')}
            />
            <QuickAction
              icon={Wand2} title="Piloto automático"
              description="Recupera produtos em queda"
              onClick={() => navigate('/autopilot')}
            />
          </div>
        </div>

        {/* Charts + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Market Growth */}
          <Card className="border-zinc-200 lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                <TrendingUp className="w-4 h-4 text-zinc-500" />
                Crescimento do mercado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {marketChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={marketChartData}>
                    <defs>
                      <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#18181B" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#18181B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#A1A1AA" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#A1A1AA" />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke="#18181B" fill="url(#colorGrowth)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div
                  className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/70 p-8 text-center"
                  data-testid="dashboard-market-growth-empty"
                >
                  <Radar className="w-9 h-9 mx-auto mb-3 text-zinc-300" strokeWidth={1.5} />
                  <p className="text-sm font-semibold text-zinc-700">
                    Aguardando dados de mercado do Collector
                  </p>
                  <p className="text-xs text-zinc-500 mt-1.5 max-w-sm mx-auto leading-relaxed">
                    A série histórica de crescimento será exibida assim que o Collector Central enviar snapshots agregados.
                  </p>
                  <Button
                    onClick={() => navigate('/intelligence/collector')}
                    size="sm"
                    variant="outline"
                    className="mt-3 text-xs"
                    data-testid="dashboard-market-growth-cta"
                  >
                    Ver Collector <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                  <Activity className="w-4 h-4 text-zinc-500" />
                  Atividade recente
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/approvals')} className="text-zinc-500 hover:text-zinc-900 text-xs">
                  Ver todas
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentActivity.length > 0 ? (
                <div>
                  {recentActivity.map((item, i) => (
                    <ActivityItem key={i} item={item} />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-zinc-500 text-sm">
                  <Activity className="w-9 h-9 mx-auto mb-3 text-zinc-300" strokeWidth={1.5} />
                  Nenhuma atividade ainda
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Module Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                <BarChart3 className="w-4 h-4 text-zinc-500" />
                Status dos módulos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={moduleData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#A1A1AA" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#A1A1AA" />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {moduleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#18181B', '#3F3F46', '#71717A', '#A1A1AA'][index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Modules Overview */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                <ShoppingBag className="w-4 h-4 text-zinc-500" />
                Módulos do sistema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { icon: Sparkles, label: 'Criador de Anúncios IA', path: '/listing-creator', status: 'Ativo' },
                  { icon: Copy, label: 'Copiador de Anúncios', path: '/listing-copier', status: 'Ativo' },
                  { icon: Megaphone, label: 'ADS Intelligence', path: '/ads-intelligence', status: 'Ativo' },
                  { icon: Wand2, label: 'Auto Piloto', path: '/autopilot', status: 'Ativo' },
                  // Radar de Mercado (Missão WhatsApp Omnichannel): atalho
                  // removido da experiência principal -- rota, código e
                  // dados continuam intactos em /market-radar.
                ].map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <button key={i} type="button" className="w-full flex items-center justify-between p-2.5 hover:bg-zinc-50 rounded-lg cursor-pointer text-left transition-colors" onClick={() => navigate(m.path)}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-zinc-500" strokeWidth={1.8} />
                        <span className="text-sm text-zinc-700">{m.label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                        <span className="text-xs text-emerald-600">{m.status}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vendas, conversão, devolução e produtos -- Missão Autopiloto
            Simples: tudo aqui vem de endpoint real (integrations meli/shopee
            dashboard, integrations status). Sem dado real disponível: estado
            vazio honesto, nunca um número calculado sem lastro. */}
        <div>
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <p className="bk-eyebrow mb-1.5">Marketplaces</p>
              <h2 className="bk-section-title text-lg">Vendas e desempenho real</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {/* Vendas */}
            <Card className="border-zinc-200" data-testid="dashboard-sales-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                  <DollarSign className="w-4 h-4 text-zinc-500" />
                  Vendas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!anyConnected ? (
                  <EmptyState
                    icon={Store}
                    title="Nenhuma conta conectada"
                    description="Conecte o Mercado Livre e/ou a Shopee para ver aqui a receita e os pedidos reais dessas contas."
                    action={
                      <Button size="sm" onClick={() => navigate('/integrations')} data-testid="dashboard-sales-connect">
                        Ir para Integrações <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    }
                  />
                ) : !hasOrderData ? (
                  <EmptyState
                    icon={ShoppingBag}
                    title="Ainda não há pedidos sincronizados"
                    description="A conta está conectada, mas ainda não sincronizou nenhum pedido. Rode uma sincronização em Integrações."
                    action={
                      <Button size="sm" variant="outline" onClick={() => navigate('/integrations')} data-testid="dashboard-sales-sync">
                        Ir para Integrações <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    }
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <StatMini
                        label="Receita (sem cancelados)"
                        value={`R$ ${netRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                        hint={totalCancelReturn > 0 ? `Exclui ${totalCancelReturn} pedido(s) cancelado(s)/devolvido(s)` : undefined}
                      />
                      <StatMini label="Pedidos" value={totalOrders} />
                      <StatMini label="Últimos 7 dias" value={ordersLast7d} />
                    </div>
                    <div className="space-y-1.5 pt-1">
                      {['mercadolivre', 'shopee'].map((mkt) => {
                        const connected = mkt === 'mercadolivre' ? meliConnected : shopeeConnected;
                        const orders = mkt === 'mercadolivre' ? meliOrders : shopeeOrders;
                        const netForMkt = mkt === 'mercadolivre' ? meliNetRevenue : shopeeNetRevenue;
                        return (
                          <div key={mkt} className="flex items-center justify-between text-xs py-1.5 border-t border-zinc-100">
                            <span className="text-zinc-600">{MKT_LABELS[mkt]}</span>
                            {connected ? (
                              <span className="text-zinc-500">
                                {orders?.total || 0} pedido(s) · R$ {netForMkt.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span className="text-zinc-400">Não conectado</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Conversão -- sem endpoint que cruze impressões/visitas com
                vendas, então nunca calculamos uma taxa aqui. */}
            <Card className="border-zinc-200" data-testid="dashboard-conversion-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                  <Percent className="w-4 h-4 text-zinc-500" />
                  Conversão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EmptyState
                  icon={Percent}
                  title="Indisponível"
                  description="Calcular conversão exige cruzar impressões/visitas com vendas -- esse dado ainda não é agregado por nenhuma API do Bunker hoje, então preferimos não estimar um número sem lastro."
                />
              </CardContent>
            </Card>

            {/* Cancelamento / devolução */}
            <Card className="border-zinc-200" data-testid="dashboard-returns-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                  <Undo2 className="w-4 h-4 text-zinc-500" />
                  Cancelamento e devolução
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!anyConnected ? (
                  <EmptyState
                    icon={Undo2}
                    title="Conecte uma conta para ver isso aqui"
                    description="Sem Mercado Livre ou Shopee conectados não há pedido real para calcular cancelamento ou devolução."
                    action={
                      <Button size="sm" variant="outline" onClick={() => navigate('/integrations')} data-testid="dashboard-returns-connect">
                        Ir para Integrações <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    }
                  />
                ) : !hasOrderData ? (
                  <EmptyState
                    icon={Undo2}
                    title="Ainda não há dados suficientes"
                    description="Nenhum pedido sincronizado ainda para calcular cancelamento ou devolução."
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <StatMini label="Cancelados/devolvidos" value={totalCancelReturn} />
                      <StatMini label="% do total de pedidos" value={`${cancelReturnPct.toFixed(1)}%`} />
                    </div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Mercado Livre só expõe cancelamento no próprio pedido (devolução formal fica em outra fonte, ainda não
                      ligada aqui); Shopee inclui cancelado, em cancelamento e a devolver.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Produtos: mais vendidos (real) + maior crescimento (indisponível) */}
            <Card className="border-zinc-200" data-testid="dashboard-top-products-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
                  <Trophy className="w-4 h-4 text-zinc-500" />
                  Produtos: mais vendidos e maior crescimento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-2">Mais vendidos</p>
                  {!anyConnected || !topProducts.length ? (
                    <p className="text-xs text-zinc-400 py-2">
                      {!anyConnected ? 'Conecte uma conta para ver o ranking real de produtos vendidos.' : 'Nenhum produto com venda sincronizada ainda.'}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {topProducts.map((p, i) => (
                        <div key={`${p.marketplace}-${p.external_id || i}`} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-zinc-300 font-bold w-4">#{i + 1}</span>
                            <span className="truncate text-zinc-700">{p.title || p.external_id}</span>
                            <Badge variant="outline" className="text-[9px] flex-shrink-0">{MKT_LABELS[p.marketplace]}</Badge>
                          </div>
                          <span className="text-zinc-500 flex-shrink-0">{p.sold_quantity || 0} vendidos</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="pt-3 border-t border-zinc-100">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-2">Maior crescimento</p>
                  <p className="text-xs text-zinc-400 py-1">
                    Indisponível -- precisa de um histórico de vendas por produto ao longo do tempo, que nenhuma API atual agrega ainda.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};
